(() => {
  'use strict';

  const ROOT_SELECTOR = '.template-index .banner-lookbook';
  const HOTSPOT_SELECTOR = '.grid-wrap > .grid-wrapper:first-child .st-hotspot';
  const OPEN_CLASS = 'open';

  const asElement = (target) => target instanceof Element ? target : null;
  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

  const getSectionKey = (root, index) => {
    const section = root.closest('.shopify-section');
    const raw = section?.id || `lookbook-${index}`;
    return raw.replace(/[^a-zA-Z0-9_-]/g, '-');
  };

  const initialiseLookbook = (root, rootIndex = 0) => {
    if (!(root instanceof HTMLElement)) return;
    if (root.dataset.zyviraLookbookReady === 'true') return;

    const primaryWrapper = root.querySelector('.grid-wrap > .grid-wrapper:first-child');
    const secondaryWrapper = root.querySelector('.grid-wrap > .grid-wrapper:nth-child(2)');
    const imageBanner = primaryWrapper?.querySelector(':scope > .banner-block');
    const hotspots = [...root.querySelectorAll(HOTSPOT_SELECTOR)];

    if (!primaryWrapper || !imageBanner || !hotspots.length) return;

    root.dataset.zyviraLookbookReady = 'true';
    const sectionKey = getSectionKey(root, rootIndex);
    let activeHotspot = null;
    let resizeFrame = 0;

    const getParts = (hotspot) => ({
      trigger: hotspot?.querySelector(':scope > .st-btn'),
      card: hotspot?.querySelector(':scope > .st-content-parent')
    });

    const setOpenSpace = (pixels = 0) => {
      primaryWrapper.style.setProperty(
        '--zyv-lookbook-open-space',
        `${Math.max(0, Math.ceil(pixels))}px`
      );
    };

    const isStackedLayout = () => {
      if (!secondaryWrapper) return false;
      const firstRect = primaryWrapper.getBoundingClientRect();
      const secondRect = secondaryWrapper.getBoundingClientRect();
      return Math.abs(firstRect.left - secondRect.left) < 12;
    };

    const syncSlider = (index) => {
      const sliderElement = root.querySelector('.banner-slider.swiper');
      const swiper = sliderElement?.swiper;
      if (swiper && typeof swiper.slideTo === 'function') {
        swiper.slideTo(index);
      }
    };

    const positionCard = (hotspot) => {
      if (!hotspot?.classList.contains(OPEN_CLASS)) return;
      const { card } = getParts(hotspot);
      if (!(card instanceof HTMLElement)) return;

      card.style.setProperty('--zyv-lookbook-card-shift-x', '0px');
      card.style.setProperty('--zyv-lookbook-arrow-left', '50%');
      setOpenSpace(0);

      const hotspotRect = hotspot.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      const viewportPadding = window.innerWidth <= 767 ? 10 : 14;
      const safeLeft = Math.max(viewportPadding, rootRect.left + 4);
      const safeRight = Math.min(window.innerWidth - viewportPadding, rootRect.right - 4);
      const hotspotCentre = hotspotRect.left + (hotspotRect.width / 2);
      const desiredLeft = hotspotCentre - (cardRect.width / 2);
      const maximumLeft = Math.max(safeLeft, safeRight - cardRect.width);
      const clampedLeft = clamp(desiredLeft, safeLeft, maximumLeft);
      const shift = clampedLeft - desiredLeft;
      const arrowLeft = clamp(hotspotCentre - clampedLeft, 22, cardRect.width - 22);

      card.style.setProperty('--zyv-lookbook-card-shift-x', `${shift}px`);
      card.style.setProperty('--zyv-lookbook-arrow-left', `${arrowLeft}px`);

      window.requestAnimationFrame(() => {
        if (!hotspot.classList.contains(OPEN_CLASS)) return;
        if (!isStackedLayout()) {
          setOpenSpace(0);
          return;
        }

        const adjustedCardRect = card.getBoundingClientRect();
        const bannerRect = imageBanner.getBoundingClientRect();
        const extraSpace = clamp(adjustedCardRect.bottom - bannerRect.bottom + 18, 0, 240);
        setOpenSpace(extraSpace);
      });
    };

    const setHotspotState = (hotspot, shouldOpen) => {
      const { trigger, card } = getParts(hotspot);
      hotspot.classList.toggle(OPEN_CLASS, shouldOpen);

      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', String(shouldOpen));
      }
      if (card instanceof HTMLElement) {
        card.setAttribute('aria-hidden', String(!shouldOpen));
      }
    };

    const closeAll = ({ restoreFocus = false } = {}) => {
      const previous = activeHotspot;
      hotspots.forEach((hotspot) => setHotspotState(hotspot, false));
      activeHotspot = null;
      setOpenSpace(0);

      if (restoreFocus && previous) {
        const { trigger } = getParts(previous);
        trigger?.focus({ preventScroll: true });
      }
    };

    const openHotspot = (hotspot) => {
      const index = hotspots.indexOf(hotspot);
      hotspots.forEach((item) => setHotspotState(item, item === hotspot));
      activeHotspot = hotspot;
      syncSlider(index);
      window.requestAnimationFrame(() => positionCard(hotspot));
    };

    const toggleHotspot = (hotspot) => {
      if (activeHotspot === hotspot && hotspot.classList.contains(OPEN_CLASS)) {
        closeAll();
      } else {
        openHotspot(hotspot);
      }
    };

    hotspots.forEach((hotspot, index) => {
      const { trigger, card } = getParts(hotspot);
      if (!(trigger instanceof HTMLElement) || !(card instanceof HTMLElement)) return;

      const cardId = `ZyviraLookbookCard-${sectionKey}-${index + 1}`;
      const productTitle = card.querySelector('h6 a')?.textContent?.trim();

      hotspot.dataset.zyviraHotspotIndex = String(index);
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('tabindex', '0');
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', cardId);
      trigger.setAttribute(
        'aria-label',
        productTitle ? `View ${productTitle}` : 'View related product'
      );

      card.id = cardId;
      card.setAttribute('role', 'region');
      card.setAttribute('aria-label', productTitle || 'Related product');
      card.setAttribute('aria-hidden', 'true');
    });

    /* Capture the hotspot click before the theme's legacy global jQuery handler. */
    root.addEventListener('click', (event) => {
      const target = asElement(event.target);
      const trigger = target?.closest('.st-btn');
      if (!trigger || !root.contains(trigger)) return;

      const hotspot = trigger.closest('.st-hotspot');
      if (!hotspot || !hotspots.includes(hotspot)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      toggleHotspot(hotspot);
    }, true);

    root.addEventListener('keydown', (event) => {
      const target = asElement(event.target);
      const trigger = target?.closest('.st-btn');
      if (!trigger || !root.contains(trigger)) return;

      const hotspot = trigger.closest('.st-hotspot');
      if (!hotspot || !hotspots.includes(hotspot)) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        toggleHotspot(hotspot);
      }
    }, true);

    document.addEventListener('click', (event) => {
      if (!activeHotspot) return;
      const target = asElement(event.target);
      if (target?.closest('.st-hotspot') === activeHotspot) return;
      closeAll();
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && activeHotspot) {
        event.preventDefault();
        closeAll({ restoreFocus: true });
      }
    });

    const queueReposition = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        if (activeHotspot) positionCard(activeHotspot);
      });
    };

    window.addEventListener('resize', queueReposition, { passive: true });
    window.addEventListener('orientationchange', queueReposition, { passive: true });

    root.querySelectorAll('img').forEach((image) => {
      if (!image.complete) {
        image.addEventListener('load', queueReposition, { once: true });
      }
    });
  };

  const initialiseAll = (scope = document) => {
    const roots = scope.matches?.(ROOT_SELECTOR)
      ? [scope]
      : [...scope.querySelectorAll(ROOT_SELECTOR)];
    roots.forEach(initialiseLookbook);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(), { once: true });
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target instanceof Element ? event.target : document;
    initialiseAll(section);
  });
})();
